/**
 * Signing out after a spell without activity (asked for 2026-09-29).
 *
 * Any click, key, scroll or touch counts as activity, in any tab of the app:
 * the time of the last one is kept in localStorage, which every tab shares.
 * Two minutes before the limit a notice counts down, with a button to stay;
 * at the limit the session is ended and the page starts afresh at sign-in,
 * with nothing of the account left in memory.
 *
 * While someone is active, the session is renewed every couple of minutes
 * (the API's own reads never renew it - apps/api/src/auth.ts). A session nobody
 * renews expires on the server a little after this limit, so closing the
 * browser does not leave it open either.
 */
import { useEffect, useRef, useState } from 'react';
import { api, type Site } from './api.ts';
import { authClient } from './auth-client.ts';
import { t } from './strings.ts';

const KEY = 'fmc.lastActive';
const WARN_MS = 2 * 60_000;
const RENEW_MS = 2 * 60_000;
const EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart', 'scroll'] as const;

let siteCache: Promise<Site> | null = null;
/** The public settings (GET /api/v1/site), fetched once per page load. */
export function useSite(): Site | null {
  const [site, setSite] = useState<Site | null>(null);
  useEffect(() => {
    siteCache ??= api.site();
    siteCache.then(setSite).catch(() => { siteCache = null; });
  }, []);
  return site;
}

const stored = () => {
  try {
    return Number(localStorage.getItem(KEY)) || 0;
  } catch {
    return 0;
  }
};
const store = (ms: number) => {
  try {
    localStorage.setItem(KEY, String(ms));
  } catch { /* private window: this tab keeps its own time */ }
};

/** Someone has just signed in: their quiet time starts now. */
export const markActive = () => store(Date.now());

/**
 * Sign out, and start the page afresh: nothing of the account stays in memory.
 * "Sign out" lands on the front page (2026-10-11); signed out for being away,
 * it is the sign-in form, which then goes back where the person was.
 */
/**
 * Sign out, from any main page (2026-10-19, the owner: "i need a sign-out button on every
 * main page"). `canLeave` returning false keeps the page - the editor asks first when there
 * are changes not saved yet.
 */
export function SignOutButton({ className = '', canLeave }: { className?: string; canLeave?: () => boolean }) {
  return (
    <button type="button" className={`sign-out no-print ${className}`.trim()}
      onClick={() => { if (!canLeave || canLeave()) void signOutAndLeave(); }}>
      {t('signOut')}
    </button>
  );
}

export async function signOutAndLeave(to = '/') {
  await authClient.signOut().catch(() => {});
  location.assign(to);
}

/** Watches for activity; warns, then signs out after `minutes` without any. */
export function IdleGuard({ minutes, since }: { minutes: number; since?: string | Date }) {
  const limit = minutes * 60_000;
  const sinceMs = since ? new Date(since).getTime() || 0 : 0;
  const [left, setLeft] = useState<number | null>(null);
  const last = useRef(0);

  useEffect(() => {
    // The last activity this browser remembers - unless a sign-in came after it (someone else
    // on a shared device, say). A browser that remembers nothing starts counting now: the
    // server has already ended any session left idle for long (apps/api/src/auth.ts).
    const kept = stored();
    last.current = kept ? Math.max(kept, sinceMs) : Date.now();
    if (!kept) store(last.current);
    let written = 0, renewed = Date.now(), leaving = false;

    const active = () => {
      const now = Date.now();
      last.current = now;
      if (now - written > 5_000) { written = now; store(now); }
    };
    const tick = () => {
      if (leaving) return;
      const now = Date.now();
      const quiet = now - Math.max(last.current, stored());
      if (quiet >= limit) {
        leaving = true;
        void signOutAndLeave(`/signin?idle=1&next=${encodeURIComponent(location.pathname + location.search)}`);
        return;
      }
      setLeft(quiet >= limit - WARN_MS ? limit - quiet : null);
      // active since the last renewal: keep the session going
      if (last.current > renewed && now - renewed >= RENEW_MS) {
        renewed = now;
        void authClient.getSession().catch(() => {});
      }
    };

    for (const e of EVENTS) addEventListener(e, active, { capture: true, passive: true });
    document.addEventListener('visibilitychange', tick);
    const timer = setInterval(tick, 1_000);
    tick();
    return () => {
      for (const e of EVENTS) removeEventListener(e, active, { capture: true });
      document.removeEventListener('visibilitychange', tick);
      clearInterval(timer);
    };
  }, [limit, sinceMs]);

  if (left === null) return null;
  const s = Math.ceil(left / 1000);
  return (
    <div className="idle-veil" role="presentation">
      <div className="idle-dialog" role="alertdialog" aria-labelledby="idle-title" aria-describedby="idle-body">
        <h2 id="idle-title">{t('idleTitle')}</h2>
        <p id="idle-body">{t('idleBody', { time: `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` })}</p>
        <div className="row">
          <button className="primary" autoFocus onClick={() => { markActive(); last.current = Date.now(); setLeft(null); }}>
            {t('idleStay')}
          </button>
          <button onClick={() => signOutAndLeave()}>{t('signOut')}</button>
        </div>
      </div>
    </div>
  );
}
